import { useMemo, useState } from 'react'
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  Copy,
  Database,
  FileText,
  Filter,
  GitBranch,
  LayoutTemplate,
  Map as MapIcon,
  MousePointer2,
  Network,
  PanelRight,
  RefreshCw,
  Search,
  ShieldCheck,
  Trash2,
  type LucideIcon,
} from 'lucide-react'
import { useEditorStore } from '@/features/page-editor/store/editor-store'
import { useStudioStore } from '@/store/studio-store'
import { cn } from '@/lib/utils'
import {
  formatRuntimeContractHeadline,
  summarizeRuntimeContract,
  type RuntimeContractSummary,
} from '@/features/validation/runtime-contract-summary'
import { getSelectionTitle, useStudioSelectionContext, type IdeSelectionContext } from '../selection-context'
import {
  buildActionFlowOntologyModel,
  inferDefaultPageEventType,
  type FlowOntologyDecision,
} from '@/features/flow-editor/services/actionflow-ontology-ux'
import {
  buildStudioProofRecordsFromProject,
  removeProjectBackedStudioProofRecord,
  removeStoredStudioProofRecord,
  type StudioProofRecord,
  type StudioProofRecordKind,
  type StudioProofRecordStatus,
} from './proof-records'
import {
  collectFallbackPromotionControlTowerModels,
  type ScopedFallbackPromotionModel,
} from '@/features/validation/fallback-promotion-control-tower-model'
import { useStudioProjectOutputKind } from '../components/artifact-mode-switcher'
import { activateStudioAuthorityTask } from '../studio-authority-activation'

type OntologyMode = 'model' | 'gaps' | 'graph' | 'contracts' | 'proofs'
type NodeTone = 'ready' | 'review' | 'muted'
type GapTone = 'blocker' | 'review' | 'info'
type RelationFilter = 'all' | NodeTone
type ProofRecordFilter = 'all' | StudioProofRecordStatus

interface StudioProjectLike {
  manifest?: unknown
  blocks?: Record<string, unknown>
  flows?: Record<string, unknown>
  data?: unknown
  tokens?: unknown
  [key: string]: unknown
}

interface ModelNode {
  id: string
  label: string
  detail?: string
  tone: NodeTone
}

interface ModelLane {
  id: string
  title: string
  subtitle: string
  icon: LucideIcon
  count: number
  nodes: ModelNode[]
}

interface ModelGap {
  id: string
  title: string
  detail: string
  tone: GapTone
  actionLabel: string
  action: 'page' | 'actionflow' | 'data' | 'validator' | 'graph'
}

interface FlowSummary {
  name: string
  trigger?: string
  bound: boolean
  routeId?: string
}

interface PageSummary {
  id: string
  label: string
  route: string
  active: boolean
  sourceFile?: string
  metadata: Record<string, unknown>
}

interface ModelRelation {
  id: string
  label: string
  detail: string
  source: {
    title: string
    detail: string
  }
  target: {
    title: string
    detail: string
  }
  tone: NodeTone
}

interface ApplicationModel {
  pageCount: number
  blockCount: number
  componentCount: number
  variableCount: number
  datasourceCount: number
  flowCount: number
  eventCount: number
  designFactCount: number
  ontologyMatching: ProjectOntologyMatchingSummary
  fallbackPromotionModels: ScopedFallbackPromotionModel[]
  boundFlowCount: number
  unboundFlows: FlowSummary[]
  lanes: ModelLane[]
  relations: ModelRelation[]
  gaps: ModelGap[]
}

interface ProjectOntologyMatchingSummary {
  matchingPlanPresent: boolean
  compilerContractPresent: boolean
  evidenceExpected: boolean
  hardConstraints: string[]
  decisionCount: number
  blockedDecisionCount: number
  candidateDecisionCount: number
  actionFlowValidationRequired: boolean
  compilerStatus?: string
  compilerEntrypoints: string[]
  projectPlanDesignSystemId?: string
  projectPlanTemplateId?: string
  labels: string[]
  status: NodeTone
}

const MODES: Array<{ id: OntologyMode; label: string }> = [
  { id: 'model', label: 'Structure' },
  { id: 'gaps', label: 'Coverage Issues' },
  { id: 'graph', label: 'Relationships' },
  { id: 'contracts', label: 'Rules' },
  { id: 'proofs', label: 'Checks' },
]

const RELATION_FILTERS: Array<{ id: RelationFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'ready', label: 'Ready' },
  { id: 'review', label: 'Review' },
  { id: 'muted', label: 'Static' },
]

/**
 * Current-app context surface.
 *
 * Export name stays LibrarySurface because the rail id is persisted as
 * "library" in older workspace state and deep links.
 */
export function LibrarySurface() {
  const outputKind = useStudioProjectOutputKind()
  const [mode, setMode] = useState<OntologyMode>(() => getInitialOntologyMode())
  const [relationQuery, setRelationQuery] = useState('')
  const [relationFilter, setRelationFilter] = useState<RelationFilter>('all')
  const [selectedRelationId, setSelectedRelationId] = useState<string | null>(null)
  const [proofRecordRevision, setProofRecordRevision] = useState(0)
  const [proofSyncing, setProofSyncing] = useState(false)
  const [proofSyncMessage, setProofSyncMessage] = useState<string | null>(null)
  const currentProjectId = useStudioStore((s) => s.currentProjectId)
  const currentProject = useStudioStore((s) => s.currentProject) as StudioProjectLike | null
  const projectMeta = useStudioStore((s) => s.projectMeta)
  const resourcesSelection = useStudioStore((s) => s.resourcesSelection)
  const resourcesDrawerOpen = useStudioStore((s) => s.resourcesDrawerOpen)
  const setActiveRailItem = useStudioStore((s) => s.setActiveRailItem)
  const setStudioMode = useStudioStore((s) => s.setStudioMode)
  const setViewMode = useStudioStore((s) => s.setViewMode)
  const setIdeActiveTab = useStudioStore((s) => s.setIdeActiveTab)
  const addEventBinding = useStudioStore((s) => s.addEventBinding)
  const refreshProofRecords = useStudioStore((s) => s.refreshProofRecords)
  const activePageId = useEditorStore((s) => s.activePageId)
  const liveSelection = useStudioSelectionContext()
  const selection = resourcesDrawerOpen && resourcesSelection ? resourcesSelection : liveSelection

  const runtimeContract = useMemo(
    () =>
      summarizeRuntimeContract({
        manifest: asRecord(currentProject?.manifest),
        blocks: asRecord(currentProject?.blocks),
        flows: asRecord(currentProject?.flows),
        data: asRecord(currentProject?.data),
      }),
    [currentProject?.manifest, currentProject?.blocks, currentProject?.flows, currentProject?.data],
  )

  const model = useMemo(
    () => buildApplicationModel(currentProject, runtimeContract, activePageId, selection),
    [activePageId, currentProject, runtimeContract, selection],
  )
  const actionFlowOntology = useMemo(
    () => buildActionFlowOntologyModel(currentProject as never),
    [currentProject],
  )
  const filteredRelations = useMemo(
    () => filterModelRelations(model.relations, relationQuery, relationFilter),
    [model.relations, relationFilter, relationQuery],
  )
  const selectedRelation = useMemo(
    () => model.relations.find((relation) => relation.id === selectedRelationId) ?? model.relations[0] ?? null,
    [model.relations, selectedRelationId],
  )
  const proofRecords = useMemo(
    () => {
      void proofRecordRevision
      return buildStudioProofRecordsFromProject(currentProjectId, currentProject, projectMeta)
    },
    [currentProjectId, currentProject, projectMeta, proofRecordRevision],
  )

  const openPage = () => {
    setStudioMode('edit')
    setViewMode('editor')
    setIdeActiveTab('pages')
    activateStudioAuthorityTask({
      domain: 'primary',
      outputKind,
      railItem: 'editor',
    })
  }

  const openActionFlow = () => {
    setStudioMode('edit')
    setViewMode('editor')
    activateStudioAuthorityTask({
      domain: 'primary',
      outputKind,
      railItem: 'behavior',
    })
  }

  const openData = () => {
    setStudioMode('edit')
    setViewMode('editor')
    activateStudioAuthorityTask({
      domain: 'primary',
      outputKind,
      railItem: 'data',
    })
  }

  const openGraph = () => {
    setMode('graph')
  }

  const openValidator = () => {
    setStudioMode('edit')
    setViewMode('editor')
    activateStudioAuthorityTask({
      domain: 'primary',
      outputKind,
      railItem: 'evidence',
    })
  }

  const openDeploy = () => {
    setStudioMode('edit')
    setViewMode('editor')
    setActiveRailItem('deploy')
  }

  const clearLocalProofRecord = (recordId: string) => {
    removeStoredStudioProofRecord(currentProjectId, recordId)
    setProofRecordRevision((revision) => revision + 1)
  }

  const refreshServerProofRecords = async () => {
    if (proofSyncing) return
    setProofSyncing(true)
    setProofSyncMessage(null)
    try {
      const refreshed = await refreshProofRecords()
      setProofSyncMessage(refreshed ? 'Checks refreshed.' : 'Open a saved server project to refresh checks.')
      setProofRecordRevision((revision) => revision + 1)
    } catch (error) {
      console.warn('[LibrarySurface] proof record refresh failed', error)
      setProofSyncMessage('Could not refresh checks.')
    } finally {
      setProofSyncing(false)
    }
  }

  const removeProofRecord = async (recordId: string) => {
    if (proofSyncing) return
    setProofSyncing(true)
    setProofSyncMessage(null)
    try {
      await removeProjectBackedStudioProofRecord(currentProjectId, recordId)
      await refreshProofRecords()
      setProofSyncMessage('Check removed.')
      setProofRecordRevision((revision) => revision + 1)
    } catch (error) {
      console.warn('[LibrarySurface] proof record remove failed', error)
      setProofSyncMessage('Could not remove check.')
    } finally {
      setProofSyncing(false)
    }
  }

  const runGapAction = (action: ModelGap['action']) => {
    if (action === 'page') openPage()
    if (action === 'actionflow') openActionFlow()
    if (action === 'data') openData()
    if (action === 'graph') openGraph()
    if (action === 'validator') openValidator()
  }

  const bindSelectedPageFlow = (decision: FlowOntologyDecision) => {
    if (!selection?.blockName || !selection.elementId) return
    const eventType = inferDefaultPageEventType(selection.elementType)
    addEventBinding(selection.blockName, `${selection.elementId}:${eventType}`, {
      flow: decision.flowName,
    })
    openActionFlow()
  }

  const selectMode = (nextMode: OntologyMode) => {
    if (typeof window !== 'undefined' && nextMode === 'proofs') {
      window.history.replaceState(null, '', '#proof-records')
    }
    setMode(nextMode)
  }


  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <header className="shrink-0 border-b border-border px-5 py-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Network className="h-4 w-4 text-muted-foreground" />
              <h1 className="text-lg font-semibold text-foreground">App details</h1>
              <span className="rounded-md border border-border bg-muted/30 px-2 py-0.5 text-xs text-muted-foreground">
                Current app
              </span>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              See how this app is put together: pages, data, logic, design choices, needs, and checks.
            </p>
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <OntologyActionButton icon={ShieldCheck} label="Open Checks" onClick={openValidator} />
          </div>
        </div>

        <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-9">
          <ModelMetric label="Pages" value={model.pageCount} />
          <ModelMetric label="Components" value={model.componentCount} />
          <ModelMetric label="Variables" value={model.variableCount} />
          <ModelMetric label="Logic" value={model.flowCount} />
          <ModelMetric label="Links" value={model.eventCount} />
          <ModelMetric
            label="Matches"
            value={model.ontologyMatching.decisionCount}
            tone={model.ontologyMatching.status}
          />
          <ModelMetric
            label="Choices"
            value={model.fallbackPromotionModels.length}
            tone={model.fallbackPromotionModels.length > 0 ? 'review' : 'muted'}
          />
          <ModelMetric label="Coverage Issues" value={model.gaps.length} tone={model.gaps.length > 0 ? 'review' : 'ready'} />
          <ModelMetric
            label="Checks"
            value={proofRecords.length}
            tone={proofRecords.some((record) => record.status === 'blocked') ? 'review' : proofRecords.length > 0 ? 'ready' : 'muted'}
          />
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <SelectionBridge selection={selection} model={model} />
          <div
            aria-label="App context view"
            role="tablist"
            className="flex min-w-0 gap-1 overflow-x-auto rounded-md border border-border bg-muted/20 p-1"
          >
            {MODES.map((candidate) => (
              <button
                key={candidate.id}
                role="tab"
                aria-selected={mode === candidate.id}
                type="button"
                onClick={() => selectMode(candidate.id)}
                className={cn(
                  'inline-flex h-8 shrink-0 items-center rounded px-3 text-xs font-medium transition-colors',
                  mode === candidate.id
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:bg-background/70 hover:text-foreground',
                )}
              >
                {candidate.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="min-h-0 flex-1 overflow-hidden">
        {mode === 'model' && (
          <ModelMapView
            model={model}
            selection={selection}
            runtimeContract={runtimeContract}
            relations={filteredRelations}
            relationQuery={relationQuery}
            relationFilter={relationFilter}
            selectedRelation={selectedRelation}
            onRelationQueryChange={setRelationQuery}
            onRelationFilterChange={setRelationFilter}
            onSelectRelation={setSelectedRelationId}
            onOpenGraph={openGraph}
            onOpenValidator={openValidator}
          />
        )}
        {mode === 'gaps' && (
          <GapsView
            model={model}
            actionFlowDecisions={actionFlowOntology.decisions}
            selection={selection}
            onRunGapAction={runGapAction}
            onBindSelectedPageFlow={bindSelectedPageFlow}
          />
        )}
        {mode === 'graph' && (
          <ProjectGraphView
            model={model}
            relations={filteredRelations}
            relationQuery={relationQuery}
            relationFilter={relationFilter}
            selectedRelation={selectedRelation}
            onRelationQueryChange={setRelationQuery}
            onRelationFilterChange={setRelationFilter}
            onSelectRelation={setSelectedRelationId}
          />
        )}
        {mode === 'contracts' && (
          <ContractsView
            model={model}
            selection={selection}
            runtimeContract={runtimeContract}
            onOpenData={openData}
            onOpenActionFlow={openActionFlow}
          />
        )}
        {mode === 'proofs' && (
          <ProofRecordsView
            records={proofRecords}
            onOpenDeploy={openDeploy}
            onClearLocalRecord={clearLocalProofRecord}
            onRefresh={refreshServerProofRecords}
            onRemoveRecord={removeProofRecord}
            syncing={proofSyncing}
            syncMessage={proofSyncMessage}
          />
        )}
      </main>
    </div>
  )
}

function ModelMetric({
  label,
  value,
  tone = 'muted',
}: {
  label: string
  value: number
  tone?: NodeTone
}) {
  return (
    <div className="rounded-md border border-border bg-muted/15 px-3 py-2">
      <div className="text-[11px] font-medium text-muted-foreground">{label}</div>
      <div
        className={cn(
          'mt-1 text-lg font-semibold tabular-nums',
          tone === 'ready' && 'text-foreground',
          tone === 'review' && 'text-muted-foreground',
          tone === 'muted' && 'text-foreground',
        )}
      >
        {value}
      </div>
    </div>
  )
}

function SelectionBridge({
  selection,
  model,
}: {
  selection: IdeSelectionContext | null
  model: ApplicationModel
}) {
  return (
    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 text-xs">
      <span className="inline-flex items-center gap-1 font-semibold text-foreground">
        <MousePointer2 className="h-3.5 w-3.5" />
        {getSelectionTitle(selection)}
      </span>
      <span className="min-w-0 max-w-xl truncate font-mono text-muted-foreground">
        {selection ? `${selection.label} · ${selection.detail}` : 'No selected page element'}
      </span>
      <span className="rounded border border-border bg-muted/30 px-2 py-1 text-muted-foreground">
        {model.boundFlowCount}/{model.flowCount} page-triggered flows
      </span>
    </div>
  )
}

function OntologyActionButton({
  icon: Icon,
  label,
  onClick,
}: {
  icon: LucideIcon
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-9 items-center gap-2 rounded-md border border-border bg-background px-3 text-sm font-medium text-foreground transition-colors hover:bg-muted"
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  )
}

function ModelMapView({
  model,
  selection,
  runtimeContract,
  relations,
  relationQuery,
  relationFilter,
  selectedRelation,
  onRelationQueryChange,
  onRelationFilterChange,
  onSelectRelation,
  onOpenGraph,
  onOpenValidator,
}: {
  model: ApplicationModel
  selection: IdeSelectionContext | null
  runtimeContract: RuntimeContractSummary
  relations: ModelRelation[]
  relationQuery: string
  relationFilter: RelationFilter
  selectedRelation: ModelRelation | null
  onRelationQueryChange: (query: string) => void
  onRelationFilterChange: (filter: RelationFilter) => void
  onSelectRelation: (relationId: string) => void
  onOpenGraph: () => void
  onOpenValidator: () => void
}) {
  return (
    <div className="grid h-full min-h-0 gap-4 overflow-y-auto p-5 xl:grid-cols-[220px_minmax(0,1fr)_320px]">
      <ModelElementPanel model={model} />

      <RelationshipCanvas
        relations={relations}
        relationQuery={relationQuery}
        relationFilter={relationFilter}
        selectedRelation={selectedRelation}
        onRelationQueryChange={onRelationQueryChange}
        onRelationFilterChange={onRelationFilterChange}
        onSelectRelation={onSelectRelation}
      />

      <aside className="min-h-0 rounded-md border border-border bg-muted/10">
        <div className="border-b border-border px-4 py-3">
          <h2 className="text-sm font-semibold text-foreground">Details</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            {selection ? `${selection.label} is the active context.` : 'No Page element is selected.'}
          </p>
        </div>
        <div className="space-y-3 p-4">
          <SelectedRelationInspector relation={selectedRelation} />
          <InfoRow label="Runtime" value={formatRuntimeContractHeadline(runtimeContract)} />
          <InfoRow label="Design" value={`${model.designFactCount} reviewed facts`} />
          <InfoRow label="Source match" value={formatOntologyMatchingHeadline(model.ontologyMatching)} />
          <InfoRow label="Review choices" value={`${model.fallbackPromotionModels.length}`} />
          <InfoRow label="Data" value={`${model.datasourceCount} datasource contracts`} />
          <InfoRow
            label="Flow binding"
            value={`${model.boundFlowCount} page triggers, ${model.unboundFlows.length} without page trigger`}
          />
          <div className="rounded-md border border-border bg-background p-3">
            <div className="flex items-center gap-2 text-xs font-semibold text-foreground">
              <AlertCircle className="h-3.5 w-3.5 text-muted-foreground" />
              Next review
            </div>
            <p className="mt-2 text-xs leading-5 text-muted-foreground">
              {model.gaps[0]?.title ?? 'No blocking source coverage issues detected.'}
            </p>
          </div>
          <div className="grid gap-2">
            <button
              type="button"
              onClick={onOpenGraph}
              className="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-border bg-background text-sm font-medium text-foreground hover:bg-muted"
            >
              Show relationships
              <ArrowRight className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={onOpenValidator}
              className="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-border bg-background text-sm font-medium text-foreground hover:bg-muted"
            >
              Open Checks
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>
    </div>
  )
}

function ProjectGraphView({
  model,
  relations,
  relationQuery,
  relationFilter,
  selectedRelation,
  onRelationQueryChange,
  onRelationFilterChange,
  onSelectRelation,
}: {
  model: ApplicationModel
  relations: ModelRelation[]
  relationQuery: string
  relationFilter: RelationFilter
  selectedRelation: ModelRelation | null
  onRelationQueryChange: (query: string) => void
  onRelationFilterChange: (filter: RelationFilter) => void
  onSelectRelation: (relationId: string) => void
}) {
  return (
    <div
      className="grid h-full min-h-0 gap-4 overflow-y-auto p-5 xl:grid-cols-[minmax(0,1fr)_320px]"
      data-testid="project-ontology-graph-workbench"
    >
      <RelationshipCanvas
        relations={relations}
        relationQuery={relationQuery}
        relationFilter={relationFilter}
        selectedRelation={selectedRelation}
        onRelationQueryChange={onRelationQueryChange}
        onRelationFilterChange={onRelationFilterChange}
        onSelectRelation={onSelectRelation}
      />
      <aside className="rounded-md border border-border bg-muted/10 p-4">
        <h2 className="text-sm font-semibold text-foreground">Relationship scope</h2>
        <p className="mt-2 text-xs leading-5 text-muted-foreground">
          This view uses only the current app, not saved Library items.
        </p>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <InfoRow label="Pages" value={`${model.pageCount}`} />
          <InfoRow label="Blocks" value={`${model.blockCount}`} />
          <InfoRow label="Flows" value={`${model.flowCount}`} />
          <InfoRow label="Bindings" value={`${model.eventCount}`} />
          <InfoRow label="Matches" value={`${model.ontologyMatching.decisionCount}`} />
          <InfoRow label="Fallbacks" value={`${model.fallbackPromotionModels.length}`} />
        </div>
        <div className="mt-4 rounded-md border border-border bg-background p-3">
          <div className="text-xs font-semibold text-foreground">Current view</div>
          <p className="mt-2 text-xs leading-5 text-muted-foreground">
            Use these relationships to review missing Page triggers, behavior rules, and checks.
          </p>
        </div>
      </aside>
    </div>
  )
}

function ModelElementPanel({ model }: { model: ApplicationModel }) {
  return (
    <section
      aria-label="Current app elements"
      className="min-h-0 rounded-md border border-border bg-background"
    >
      <div className="border-b border-border px-4 py-3">
        <h2 className="text-sm font-semibold text-foreground">Elements</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Current app parts, not a template list.
        </p>
      </div>
      <div className="space-y-3 p-2.5">
        <PathFinder relations={model.relations} />
        {model.lanes.map((lane) => (
          <ModelElementGroup key={lane.id} lane={lane} />
        ))}
      </div>
    </section>
  )
}

function ModelElementGroup({ lane }: { lane: ModelLane }) {
  const Icon = lane.icon
  return (
    <div className="rounded-md border border-border bg-muted/10 p-2.5">
      <div className="flex items-start gap-2">
        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border bg-background text-muted-foreground">
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center justify-between gap-2">
            <h3 className="truncate text-sm font-semibold text-foreground">{lane.title}</h3>
            <span className="shrink-0 text-sm font-semibold tabular-nums text-foreground">{lane.count}</span>
          </div>
          <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{lane.subtitle}</p>
        </div>
      </div>
      <div className="mt-3 space-y-1.5">
        {lane.nodes.length > 0 ? (
          lane.nodes.slice(0, 2).map((node) => <ModelNodeChip key={node.id} node={node} />)
        ) : (
          <div className="rounded-md border border-dashed border-border px-2 py-1.5 text-xs text-muted-foreground">
            Not defined
          </div>
        )}
      </div>
    </div>
  )
}

function PathFinder({ relations }: { relations: ModelRelation[] }) {
  const path = relations.map((relation) => relation.label).join(' -> ')
  return (
    <div className="rounded-md border border-border bg-muted/10 p-2.5" data-testid="application-path-finder">
      <div className="flex items-center gap-2 text-xs font-semibold text-foreground">
        <MapIcon className="h-3.5 w-3.5 text-muted-foreground" />
        Path Finder
      </div>
      <p className="mt-1 text-[11px] leading-4 text-muted-foreground">
        {path || 'No app relationship path yet'}
      </p>
    </div>
  )
}

function RelationshipCanvas({
  relations,
  relationQuery,
  relationFilter,
  selectedRelation,
  onRelationQueryChange,
  onRelationFilterChange,
  onSelectRelation,
}: {
  relations: ModelRelation[]
  relationQuery: string
  relationFilter: RelationFilter
  selectedRelation: ModelRelation | null
  onRelationQueryChange: (query: string) => void
  onRelationFilterChange: (filter: RelationFilter) => void
  onSelectRelation: (relationId: string) => void
}) {
  return (
    <section
      aria-label="Current app relationship map"
      data-testid="application-model-map"
      className="min-h-0 rounded-md border border-border bg-background"
    >
      <div className="border-b border-border px-4 py-3">
        <h2 className="text-sm font-semibold text-foreground">Relationships</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Read each row left to right: source, relationship, target.
        </p>
      </div>
      <div className="border-b border-border p-3">
        <label className="relative block">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            aria-label="Search relationships"
            value={relationQuery}
            onChange={(event) => onRelationQueryChange(event.target.value)}
            placeholder="Search Page, Logic, Data..."
            className="h-9 w-full rounded-md border border-border bg-background pl-8 pr-3 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-ring"
          />
        </label>
        <div className="mt-2 flex flex-wrap gap-1.5" aria-label="Filter relationships">
          {RELATION_FILTERS.map((filter) => (
            <button
              key={filter.id}
              type="button"
              onClick={() => onRelationFilterChange(filter.id)}
              className={cn(
                'h-7 rounded-md border px-2.5 text-xs font-medium transition-colors',
                relationFilter === filter.id
                  ? 'border-border bg-background text-foreground shadow-sm'
                  : 'border-border bg-muted/20 text-muted-foreground hover:bg-muted hover:text-foreground',
              )}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-2.5 p-3" data-testid="application-relationship-map">
        {relations.length > 0 ? (
          relations.map((relation) => (
            <RelationshipRow
              key={relation.id}
              relation={relation}
              selected={selectedRelation?.id === relation.id}
              onSelect={() => onSelectRelation(relation.id)}
            />
          ))
        ) : (
          <div className="rounded-md border border-dashed border-border bg-muted/10 p-4 text-sm text-muted-foreground">
            No relationships match this filter.
          </div>
        )}
      </div>
    </section>
  )
}

function RelationshipRow({
  relation,
  selected,
  onSelect,
}: {
  relation: ModelRelation
  selected: boolean
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={cn(
        'block w-full rounded-md border bg-muted/10 p-2.5 text-left transition-colors hover:border-ring/40 hover:bg-muted/30',
        selected ? 'border-ring bg-accent/40 ring-1 ring-ring/20 shadow-sm' : 'border-border',
      )}
    >
      {selected && (
        <div className="mb-2 inline-flex rounded-full border border-border bg-background px-2 py-0.5 text-[11px] font-medium text-foreground">
          Selected
        </div>
      )}
      <div className="grid items-center gap-3 lg:grid-cols-[minmax(180px,1fr)_112px_minmax(180px,1fr)]">
        <RelationshipEndpoint title={relation.source.title} detail={relation.source.detail} tone="muted" />
        <div className="flex items-center gap-2" aria-label={`${relation.source.title} ${relation.label} ${relation.target.title}`}>
          <span className="hidden h-px flex-1 bg-border lg:block" />
          <span
            className={cn(
              'inline-flex shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold',
              relation.tone === 'ready' && 'border-border bg-background text-foreground',
              relation.tone === 'review' && 'border-border bg-background text-muted-foreground',
              relation.tone === 'muted' && 'border-border bg-background text-muted-foreground',
            )}
          >
            {relation.label}
            <ArrowRight className="h-3.5 w-3.5" />
          </span>
          <span className="h-px flex-1 bg-border" />
        </div>
        <RelationshipEndpoint title={relation.target.title} detail={relation.target.detail} tone={relation.tone} />
      </div>
      <p className="mt-1.5 truncate text-xs text-muted-foreground">{relation.detail}</p>
    </button>
  )
}

function SelectedRelationInspector({ relation }: { relation: ModelRelation | null }) {
  if (!relation) {
    return (
      <div className="rounded-md border border-dashed border-border bg-background p-3 text-xs text-muted-foreground">
        Select a relationship to inspect.
      </div>
    )
  }

  return (
    <div className="rounded-md border border-border bg-muted/20 p-3" data-testid="selected-relation-inspector">
      <div className="text-[11px] font-medium text-muted-foreground">Chosen relationship</div>
      <div className="mt-1 truncate text-sm font-semibold text-foreground">
        {`${relation.source.title} -> ${relation.target.title}`}
      </div>
      <div className="mt-2 inline-flex rounded-full border border-border bg-background px-2 py-1 text-xs font-medium text-foreground">
        {relation.label}
      </div>
      <p className="mt-2 text-xs leading-5 text-muted-foreground">{relation.detail}</p>
    </div>
  )
}

function RelationshipEndpoint({
  title,
  detail,
  tone,
}: {
  title: string
  detail: string
  tone: NodeTone
}) {
  return (
    <div
      className={cn(
        'min-w-0 rounded-md border px-3 py-2',
        tone === 'ready' && 'border-border bg-background',
        tone === 'review' && 'border-border bg-background',
        tone === 'muted' && 'border-border bg-background',
      )}
    >
      <div className="truncate text-sm font-semibold text-foreground">{title}</div>
      <div className="mt-1 truncate text-xs text-muted-foreground">{detail}</div>
    </div>
  )
}

function ModelNodeChip({ node }: { node: ModelNode }) {
  return (
    <div
      className={cn(
        'min-w-0 rounded-md border px-2 py-1.5 text-xs',
        node.tone === 'ready' && 'border-border bg-background text-foreground',
        node.tone === 'review' && 'border-border bg-background text-muted-foreground',
        node.tone === 'muted' && 'border-border bg-background text-muted-foreground',
      )}
    >
      <div className="truncate font-medium">{node.label}</div>
      {node.detail && <div className="mt-1 truncate opacity-80">{node.detail}</div>}
    </div>
  )
}

function GapsView({
  model,
  actionFlowDecisions,
  selection,
  onRunGapAction,
  onBindSelectedPageFlow,
}: {
  model: ApplicationModel
  actionFlowDecisions: FlowOntologyDecision[]
  selection: IdeSelectionContext | null
  onRunGapAction: (action: ModelGap['action']) => void
  onBindSelectedPageFlow: (decision: FlowOntologyDecision) => void
}) {
  const selectedPageElement = Boolean(selection?.blockName && selection.elementId)
  const readyFacts = [
    { label: 'Pages', value: model.pageCount },
    { label: 'Components', value: model.componentCount },
    { label: 'Variables', value: model.variableCount },
    { label: 'Logic flows', value: model.flowCount },
    { label: 'Page triggers', value: model.eventCount },
    { label: 'Data sources', value: model.datasourceCount },
  ]

  return (
    <div className="h-full min-h-0 overflow-y-auto p-5">
      <div className="mx-auto max-w-5xl">
        <div className="mb-4">
          <h2 className="text-base font-semibold text-foreground">Coverage Issues</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Review source coverage issues before changing a complex app.
          </p>
        </div>
        {actionFlowDecisions.length > 0 && (
          <section className="mb-5 rounded-md border border-border bg-muted/10 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h3 className="text-sm font-semibold text-foreground">Logic review queue</h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  Review generated logic choices for this app, then connect only the current Page element when the trigger is intentional.
                </p>
              </div>
              <span className="rounded-md border border-border bg-background px-2 py-1 text-xs text-muted-foreground">
                {selectedPageElement ? 'Page element chosen' : 'Choose a Page element to connect logic'}
              </span>
            </div>
            <div className="mt-3 grid gap-2">
              {actionFlowDecisions.slice(0, 6).map((decision) => (
                <ActionFlowDecisionRow
                  key={decision.flowName}
                  decision={decision}
                  canBind={selectedPageElement}
                  onOpenActionFlow={() => onRunGapAction('actionflow')}
                  onBindSelectedPageFlow={() => onBindSelectedPageFlow(decision)}
                />
              ))}
            </div>
          </section>
        )}
        {model.gaps.length === 0 ? (
          <div className="rounded-md border border-border bg-muted/10 p-5" data-testid="project-ontology-no-open-gaps">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                  <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
                  No open coverage issues
                </div>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">
                  The current app has Page structure, behavior links, logic, and design details ready for review.
                </p>
              </div>
              <span className="rounded-md border border-border bg-background px-2 py-1 text-xs text-muted-foreground">
                Current app only
              </span>
            </div>
            <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {readyFacts.map((fact) => (
                <div key={fact.label} className="rounded-md border border-border bg-background px-3 py-2">
                  <div className="text-[11px] font-medium text-muted-foreground">{fact.label}</div>
                  <div className="mt-1 font-mono text-sm font-semibold text-foreground">{fact.value}</div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="grid gap-3">
            {model.gaps.map((gap) => (
              <div key={gap.id} className="rounded-md border border-border bg-background p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <GapIcon tone={gap.tone} />
                      <h3 className="text-sm font-semibold text-foreground">{gap.title}</h3>
                      <span className="rounded border border-border bg-muted/30 px-1.5 py-0.5 text-[11px] text-muted-foreground">
                        {gap.tone}
                      </span>
                    </div>
                    <p className="mt-2 text-sm leading-6 text-muted-foreground">{gap.detail}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => onRunGapAction(gap.action)}
                    className="inline-flex h-9 shrink-0 items-center gap-2 rounded-md border border-border bg-background px-3 text-sm font-medium text-foreground hover:bg-muted"
                  >
                    {gap.actionLabel}
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {model.unboundFlows.length > 0 && (
          <section className="mt-5 rounded-md border border-border bg-muted/10 p-4">
            <h3 className="text-sm font-semibold text-foreground">Logic without Page trigger</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              These are not automatically wrong. Connect them only when a Page element should start the behavior.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {model.unboundFlows.slice(0, 12).map((flow) => (
                <span key={flow.name} className="rounded-md border border-border bg-background px-2 py-1 text-xs text-muted-foreground">
                  {flow.name}
                </span>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  )
}

function ActionFlowDecisionRow({
  decision,
  canBind,
  onOpenActionFlow,
  onBindSelectedPageFlow,
}: {
  decision: FlowOntologyDecision
  canBind: boolean
  onOpenActionFlow: () => void
  onBindSelectedPageFlow: () => void
}) {
  return (
    <div
      className="rounded-md border border-border bg-background p-3"
      data-testid={`project-ontology-flow-decision-${decision.flowName}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="truncate text-sm font-semibold text-foreground">{decision.flowName}</h4>
            <span className="rounded-md border border-border bg-muted/30 px-2 py-0.5 text-[11px] text-muted-foreground">
              {decision.label}
            </span>
          </div>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">{decision.actionSummary}</p>
          <p className="mt-1 text-[11px] text-muted-foreground">{decision.helper}</p>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          <button
            type="button"
            onClick={onOpenActionFlow}
            className="inline-flex h-8 items-center rounded-md border border-border bg-background px-2.5 text-xs font-medium text-foreground hover:bg-muted"
          >
            Open logic
          </button>
          <button
            type="button"
            onClick={onBindSelectedPageFlow}
            disabled={!canBind}
            className="inline-flex h-8 items-center rounded-md border border-border bg-foreground px-2.5 text-xs font-medium text-background transition-colors hover:bg-foreground/90 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground"
          >
            Connect current Page
          </button>
        </div>
      </div>
    </div>
  )
}

function GapIcon({ tone }: { tone: GapTone }) {
  if (tone === 'blocker') return <AlertCircle className="h-4 w-4 text-destructive" />
  if (tone === 'review') return <AlertCircle className="h-4 w-4 text-muted-foreground" />
  return <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
}

function ContractsView({
  model,
  selection,
  runtimeContract,
  onOpenData,
  onOpenActionFlow,
}: {
  model: ApplicationModel
  selection: IdeSelectionContext | null
  runtimeContract: RuntimeContractSummary
  onOpenData: () => void
  onOpenActionFlow: () => void
}) {
  return (
    <div className="h-full min-h-0 overflow-y-auto p-5">
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
        <section className="rounded-md border border-border bg-background">
          <div className="border-b border-border px-4 py-3">
            <h2 className="text-sm font-semibold text-foreground">Behavior rules</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              {formatRuntimeContractHeadline(runtimeContract)}
            </p>
          </div>
          <div className="grid gap-3 p-4 md:grid-cols-2">
            <ContractGroup title="Events" values={runtimeContract.eventTypes} empty="No page events" />
            <ContractGroup title="State changes" values={runtimeContract.stateScopes} empty="No state changes" />
	            <ContractGroup title="App data" values={runtimeContract.appDataWriteTargets} empty="No app data changes" />
            <ContractGroup title="Data sources" values={runtimeContract.externalDatasourceTargets} empty="No external data sources" />
            <ContractGroup title="Tools" values={runtimeContract.webMcpToolContracts} empty="No tool contracts" />
            <ContractGroup title="Domain actions" values={runtimeContract.domainActionContracts} empty="No domain action contracts" />
	            <ContractGroup title="Named outputs" values={runtimeContract.resultNames} empty="No named outputs" />
            <ContractGroup title="Capabilities" values={runtimeContract.capabilityNotes} empty="No runtime notes" />
          </div>
        </section>

        <aside className="rounded-md border border-border bg-muted/10 p-4">
	          <h2 className="text-sm font-semibold text-foreground">Current details</h2>
          <div className="mt-3 space-y-3">
            <InfoRow label="Selection" value={selection ? `${selection.label} / ${selection.detail}` : 'None'} />
            <InfoRow label="Source match" value={formatOntologyMatchingHeadline(model.ontologyMatching)} />
            <InfoRow label="Review choices" value={`${model.fallbackPromotionModels.length}`} />
            <InfoRow label="Variables" value={`${model.variableCount}`} />
            <InfoRow label="Data sources" value={`${model.datasourceCount}`} />
	            <InfoRow label="Unlinked behavior" value={`${model.unboundFlows.length}`} />
          </div>
          <div className="mt-4 grid gap-2">
            <button
              type="button"
              onClick={onOpenData}
              className="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-border bg-background text-sm font-medium text-foreground hover:bg-muted"
            >
              Open Data
              <ArrowRight className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={onOpenActionFlow}
              className="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-border bg-background text-sm font-medium text-foreground hover:bg-muted"
            >
	              Open Behavior
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </aside>
      </div>
    </div>
  )
}

function ProofRecordsView({
  records,
  onOpenDeploy,
  onClearLocalRecord,
  onRefresh,
  onRemoveRecord,
  syncing,
  syncMessage,
}: {
  records: StudioProofRecord[]
  onOpenDeploy: () => void
  onClearLocalRecord: (recordId: string) => void
  onRefresh: () => void
  onRemoveRecord: (recordId: string) => void
  syncing: boolean
  syncMessage: string | null
}) {
  const [filter, setFilter] = useState<ProofRecordFilter>('all')
  const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null)
  const [copiedRecordId, setCopiedRecordId] = useState<string | null>(null)
  const blockedCount = records.filter((record) => record.status === 'blocked').length
  const passCount = records.filter((record) => record.status === 'pass').length
  const reviewCount = records.filter((record) => record.status === 'review').length
  const filteredRecords = filter === 'all'
    ? records
    : records.filter((record) => record.status === filter)
  const selectedRecord =
    filteredRecords.find((record) => record.id === selectedRecordId) ??
    filteredRecords[0] ??
    records[0] ??
    null

  function copyEvidence(record: StudioProofRecord) {
    const text = JSON.stringify(record.evidence && Object.keys(record.evidence).length > 0 ? record.evidence : record, null, 2)
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText(text).catch(() => undefined)
    }
    setCopiedRecordId(record.id)
    if (typeof window !== 'undefined') {
      window.setTimeout(() => setCopiedRecordId((current) => current === record.id ? null : current), 1500)
    }
  }

  return (
    <div className="h-full min-h-0 overflow-y-auto p-5">
      <div className="mx-auto max-w-6xl">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-foreground">Checks</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Launch, render, and cleanup checks attached to this Studio project.
            </p>
            {syncMessage && (
              <p className={cn(
                'mt-1 text-xs',
                syncMessage.startsWith('Could not') ? 'text-destructive' : 'text-muted-foreground',
              )}>
                {syncMessage}
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="rounded-md border border-border bg-background px-2 py-1 text-muted-foreground">
              {records.length} checks
            </span>
            <span className="rounded-md border border-border bg-background px-2 py-1 text-foreground">
              {passCount} pass
            </span>
            <span className="rounded-md border border-border bg-background px-2 py-1 text-muted-foreground">
              {reviewCount} review
            </span>
            <span className="rounded-md border border-destructive/25 bg-destructive/10 px-2 py-1 text-destructive">
              {blockedCount} blocked
            </span>
            <button
              type="button"
              onClick={onRefresh}
              disabled={syncing}
              className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border bg-background px-2.5 text-xs font-medium text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
            >
              <RefreshCw className={cn('h-3.5 w-3.5', syncing && 'animate-spin')} />
              {syncing ? 'Refreshing' : 'Refresh checks'}
            </button>
          </div>
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-md border border-border bg-muted/10 p-2">
          <span className="inline-flex items-center gap-1 px-1 text-xs font-medium text-muted-foreground">
            <Filter className="h-3.5 w-3.5" />
            Status
          </span>
          {(['all', 'pass', 'review', 'blocked'] as ProofRecordFilter[]).map((candidate) => (
            <button
              key={candidate}
              type="button"
              onClick={() => setFilter(candidate)}
              className={cn(
                'inline-flex h-7 items-center rounded-md border px-2.5 text-xs font-medium transition-colors',
                filter === candidate
                  ? 'border-border bg-background text-foreground shadow-sm'
                  : 'border-border bg-background text-muted-foreground hover:text-foreground',
              )}
            >
              {candidate}
            </button>
          ))}
        </div>

        {records.length === 0 ? (
          <div className="rounded-md border border-dashed border-border bg-muted/10 p-5">
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <ShieldCheck className="h-4 w-4 text-muted-foreground" />
              No checks yet
            </div>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              Open launch settings to refresh checks, or attach details to this app.
            </p>
            <button
              type="button"
              onClick={onOpenDeploy}
              className="mt-4 inline-flex h-9 items-center gap-2 rounded-md border border-border bg-background px-3 text-sm font-medium text-foreground hover:bg-muted"
            >
              Open launch settings
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        ) : filteredRecords.length === 0 ? (
          <div className="rounded-md border border-dashed border-border bg-muted/10 p-5">
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <ShieldCheck className="h-4 w-4 text-muted-foreground" />
              No checks for this status
            </div>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              Change the filter to see other check states attached to this project.
            </p>
          </div>
        ) : (
          <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_420px]">
            <div className="grid content-start gap-3">
              {filteredRecords.map((record) => (
                <ProofRecordCard
                  key={record.id}
                  record={record}
                  selected={selectedRecord?.id === record.id}
                  onSelect={() => setSelectedRecordId(record.id)}
                  onOpenDeploy={onOpenDeploy}
                />
              ))}
            </div>
            {selectedRecord && (
              <ProofRecordDetails
                record={selectedRecord}
                copied={copiedRecordId === selectedRecord.id}
                onCopy={() => copyEvidence(selectedRecord)}
                onOpenDeploy={onOpenDeploy}
                onClearLocalRecord={() => onClearLocalRecord(selectedRecord.id)}
                onRemoveRecord={() => onRemoveRecord(selectedRecord.id)}
                removing={syncing}
              />
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function ProofRecordCard({
  record,
  selected,
  onSelect,
  onOpenDeploy,
}: {
  record: StudioProofRecord
  selected: boolean
  onSelect: () => void
  onOpenDeploy: () => void
}) {
  return (
    <section
      className={cn(
        'rounded-md border bg-background p-4 transition-colors',
        selected ? 'border-ring ring-1 ring-ring/20' : 'border-border',
      )}
      data-testid="proof-record-card"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <ProofRecordStatusBadge status={record.status} />
            <h3 className="text-sm font-semibold text-foreground">{record.title}</h3>
            <span className="rounded border border-border bg-muted/30 px-1.5 py-0.5 text-[11px] text-muted-foreground">
              {proofRecordKindLabel(record.kind)}
            </span>
          </div>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">{record.summary}</p>
          <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-muted-foreground">
            <span className="rounded border border-border bg-muted/20 px-2 py-1">source: {record.source}</span>
            <span className="rounded border border-border bg-muted/20 px-2 py-1">created: {formatProofRecordDate(record.createdAt)}</span>
          </div>
        </div>
        {record.kind === 'production-studio-readback' && (
          <button
            type="button"
            onClick={onOpenDeploy}
            className="inline-flex h-9 shrink-0 items-center gap-2 rounded-md border border-border bg-background px-3 text-sm font-medium text-foreground hover:bg-muted"
          >
            Open launch settings
            <ArrowRight className="h-4 w-4" />
          </button>
        )}
        <button
          type="button"
          onClick={onSelect}
          className="inline-flex h-9 shrink-0 items-center gap-2 rounded-md border border-border bg-background px-3 text-sm font-medium text-foreground hover:bg-muted"
        >
          Details
          <PanelRight className="h-4 w-4" />
        </button>
      </div>

      {record.facts.length > 0 && (
        <div className="mt-4 grid gap-2 rounded-md border border-border bg-muted/10 p-3 text-xs sm:grid-cols-2 lg:grid-cols-3">
          {record.facts.slice(0, 9).map((fact) => (
            <InfoRow key={`${record.id}:${fact.label}`} label={fact.label} value={fact.value} />
          ))}
        </div>
      )}

      {(record.missing.length > 0 || record.failureReasons.length > 0) && (
        <div className="mt-4 grid gap-3 lg:grid-cols-2">
          {record.missing.length > 0 && (
            <ProofRecordIssueList title="Missing details" items={record.missing} />
          )}
          {record.failureReasons.length > 0 && (
            <ProofRecordIssueList title="Fail-closed reasons" items={record.failureReasons} />
          )}
        </div>
      )}
    </section>
  )
}

function ProofRecordDetails({
  record,
  copied,
  onCopy,
  onOpenDeploy,
  onClearLocalRecord,
  onRemoveRecord,
  removing,
}: {
  record: StudioProofRecord
  copied: boolean
  onCopy: () => void
  onOpenDeploy: () => void
  onClearLocalRecord: () => void
  onRemoveRecord: () => void
  removing: boolean
}) {
  const evidenceJson = JSON.stringify(record.evidence && Object.keys(record.evidence).length > 0 ? record.evidence : record, null, 2)
  const officeResidualSummary = buildOfficeResidualSummary(record)

  return (
    <aside className="rounded-md border border-border bg-background p-4" data-testid="proof-record-details">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <PanelRight className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold text-foreground">Check details</h3>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {proofRecordKindLabel(record.kind)} · {formatProofRecordDate(record.createdAt)}
          </p>
        </div>
        <ProofRecordStatusBadge status={record.status} />
      </div>

      <div className="mt-4 space-y-2">
        <InfoRow label="Title" value={record.title} />
        <InfoRow label="Source" value={record.source} />
        <InfoRow label="Record ID" value={record.id} />
      </div>

      {record.facts.length > 0 && (
        <div className="mt-4 grid gap-2 text-xs">
          {record.facts.map((fact) => (
            <InfoRow key={`detail:${record.id}:${fact.label}`} label={fact.label} value={fact.value} />
          ))}
        </div>
      )}

      {(record.missing.length > 0 || record.failureReasons.length > 0) && (
        <div className="mt-4 grid gap-3">
          {record.missing.length > 0 && (
            <ProofRecordIssueList title="Missing details" items={record.missing} />
          )}
          {record.failureReasons.length > 0 && (
            <ProofRecordIssueList title="Fail-closed reasons" items={record.failureReasons} />
          )}
        </div>
      )}

      {officeResidualSummary && (
        <OfficeResidualProofSummary summary={officeResidualSummary} />
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onCopy}
          className="inline-flex h-8 items-center gap-2 rounded-md border border-border bg-background px-3 text-xs font-medium text-foreground hover:bg-muted"
        >
          <Copy className="h-3.5 w-3.5" />
          {copied ? 'Copied' : 'Copy details'}
        </button>
        {record.kind === 'production-studio-readback' && (
          <button
            type="button"
            onClick={onOpenDeploy}
            className="inline-flex h-8 items-center gap-2 rounded-md border border-border bg-background px-3 text-xs font-medium text-foreground hover:bg-muted"
          >
            Open launch settings
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        )}
        <button
          type="button"
          onClick={onClearLocalRecord}
          className="inline-flex h-8 items-center gap-2 rounded-md border border-border bg-background px-3 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <Trash2 className="h-3.5 w-3.5" />
          Clear local copy
        </button>
        <button
          type="button"
          onClick={onRemoveRecord}
          disabled={removing}
          className="inline-flex h-8 items-center gap-2 rounded-md border border-destructive/30 bg-background px-3 text-xs font-medium text-destructive hover:bg-destructive/10 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Trash2 className="h-3.5 w-3.5" />
          Remove check
        </button>
      </div>

      <div className="mt-4">
        <div className="mb-2 text-xs font-semibold text-foreground">Check data</div>
        <pre className="max-h-80 overflow-auto rounded-md border border-border bg-muted/20 p-3 text-[11px] leading-5 text-muted-foreground">
          {evidenceJson}
        </pre>
      </div>
    </aside>
  )
}

interface OfficeResidualSummary {
  facts: Array<{ label: string; value: string }>
  issueClasses: string[]
  tableProfiles: string[]
  imageSamples: string[]
  paragraphSamples: string[]
}

function OfficeResidualProofSummary({ summary }: { summary: OfficeResidualSummary }) {
  return (
    <div className="mt-4 rounded-md border border-border bg-muted/10 p-3">
      <div className="text-xs font-semibold text-foreground">Office visual residuals</div>
      <div className="mt-3 grid gap-2 text-xs">
        {summary.facts.map((fact) => (
          <InfoRow key={`office-residual:${fact.label}`} label={fact.label} value={fact.value} />
        ))}
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <ProofRecordIssueList title="Issue classes" items={summary.issueClasses.length > 0 ? summary.issueClasses : ['none']} />
        <ProofRecordIssueList title="Table profiles" items={summary.tableProfiles.length > 0 ? summary.tableProfiles : ['none']} />
        <ProofRecordIssueList title="Image residual samples" items={summary.imageSamples.length > 0 ? summary.imageSamples : ['none']} />
        <ProofRecordIssueList title="Paragraph crop samples" items={summary.paragraphSamples.length > 0 ? summary.paragraphSamples : ['none']} />
      </div>
    </div>
  )
}

function buildOfficeResidualSummary(record: StudioProofRecord): OfficeResidualSummary | null {
  const evidence = asRecord(record.evidence)
  const proof = asRecord(evidence.officeVisualResidualAttributionProof || evidence)
  if (Object.keys(proof).length === 0 || proof.schemaVersion !== 'cogo.document-office-visual-residual-attribution.v1') return null

  const diagnostics = asRecord(proof.diagnostics)
  const denseFlowProof = asRecord(diagnostics.denseTableFlowProof)
  const imageProof = asRecord(diagnostics.imageResidualProof)
  const cropProof = asRecord(diagnostics.paragraphLineBoxCropProof)
  const tableProof = asRecord(diagnostics.tableResidualProfileProof)
  const byIssueClass = asRecord(asRecord(proof.totals).byIssueClass)
  const byStructuralProfile = asRecord(tableProof.byStructuralProfile)
  const facts = [
    { label: 'Format', value: stringValue(evidence.format) ?? stringValue(proof.format) ?? 'office' },
    { label: 'Status', value: stringValue(proof.status) ?? 'unknown' },
    { label: 'Diagnostic only', value: String(proof.diagnosticOnly === true) },
    { label: 'ProductionReady', value: String(evidence.targetProductionReady === true) },
    { label: 'Dense flow pages', value: arraySummary(denseFlowProof.highRiskPages) || 'none' },
  ]

  return {
    facts,
    issueClasses: Object.keys(byIssueClass).slice(0, 8),
    tableProfiles: Object.keys(byStructuralProfile).slice(0, 8),
    imageSamples: asArray(imageProof.samples)
      .slice(0, 4)
      .map((sample) => formatResidualSample(sample)),
    paragraphSamples: asArray(cropProof.samples)
      .slice(0, 4)
      .map((sample) => formatResidualSample(sample)),
  }
}

function formatResidualSample(value: unknown): string {
  const sample = asRecord(value)
  const metrics = asRecord(sample.metrics)
  const pageIndex = stringValue(sample.pageIndex) ?? String(numberValue(sample.pageIndex) ?? '?')
  const kind = stringValue(sample.kind) ?? 'layer'
  const issueClass = stringValue(sample.issueClass) ?? 'residual'
  const contribution = numberValue(metrics.contributionOfPageDiff)
  const bbox = numberValue(metrics.maxAbsBboxDeltaPx)
  return [
    `p${pageIndex}`,
    kind,
    issueClass,
    contribution !== undefined ? `contrib=${contribution.toFixed(3)}` : null,
    bbox !== undefined ? `bbox=${bbox.toFixed(1)}px` : null,
  ].filter(Boolean).join(' · ')
}

function arraySummary(value: unknown): string {
  return asArray(value)
    .map((entry) => stringValue(entry) ?? (typeof entry === 'number' && Number.isFinite(entry) ? String(entry) : null))
    .filter((entry): entry is string => Boolean(entry))
    .join(', ')
}

function ProofRecordStatusBadge({ status }: { status: StudioProofRecordStatus }) {
  return (
    <span
      className={cn(
        'inline-flex h-6 items-center rounded-full border px-2 text-[11px] font-semibold',
        status === 'pass' && 'border-border bg-muted text-foreground',
        status === 'review' && 'border-border bg-muted text-muted-foreground',
        status === 'blocked' && 'border-destructive/25 bg-destructive/10 text-destructive',
      )}
    >
      {status}
    </span>
  )
}

function ProofRecordIssueList({ title, items }: { title: string; items: string[] }) {
  return (
    <div className="rounded-md border border-border bg-muted/10 p-3">
      <div className="text-xs font-semibold text-foreground">{title}</div>
      <ul className="mt-2 space-y-1.5">
        {items.slice(0, 8).map((item) => (
          <li key={item} className="flex gap-2 text-xs text-muted-foreground">
            <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/60" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function ContractGroup({
  title,
  values,
  empty,
}: {
  title: string
  values: string[]
  empty: string
}) {
  return (
    <div className="rounded-md border border-border bg-muted/10 p-3">
      <div className="text-xs font-semibold text-foreground">{title}</div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {values.length > 0 ? (
          values.slice(0, 10).map((value) => (
            <span key={`${title}:${value}`} className="rounded-md bg-background px-2 py-1 text-xs text-muted-foreground">
              {value}
            </span>
          ))
        ) : (
          <span className="text-xs text-muted-foreground">{empty}</span>
        )}
      </div>
    </div>
  )
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 rounded-md border border-border bg-background px-3 py-2">
      <div className="text-[11px] font-medium text-muted-foreground">{label}</div>
      <div className="mt-1 truncate text-sm font-semibold text-foreground">{value}</div>
    </div>
  )
}

function proofRecordKindLabel(kind: StudioProofRecordKind): string {
  if (kind === 'production-studio-readback') return 'production launch'
  if (kind === 'native-office-parity-proof') return 'native Office parity'
  if (kind === 'browser-render-proof') return 'browser render'
  if (kind === 'design-system-proof') return 'style system'
  return 'cleanup'
}

function formatProofRecordDate(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString()
}

function getInitialOntologyMode(): OntologyMode {
  if (typeof window !== 'undefined' && window.location.hash === '#proof-records') return 'proofs'
  return 'model'
}

function buildApplicationModel(
  project: StudioProjectLike | null,
  runtimeContract: RuntimeContractSummary,
  activePageId: string | null,
  selection: IdeSelectionContext | null,
): ApplicationModel {
  const blocks = asRecord(project?.blocks)
  const flows = collectFlows(project)
  const pages = collectPages(project, activePageId)
  const blockNames = Object.keys(blocks)
  const componentCount = Object.values(blocks).reduce<number>((sum, block) => sum + countBlockComponents(block), 0)
  const variableNames = collectVariableNames(blocks, runtimeContract)
  const datasourceNames = collectDatasourceNames(project)
  const designFactCount = countDesignFacts(project)
  const ontologyMatching = buildProjectOntologyMatchingSummary(project)
  const fallbackPromotionModels = collectFallbackPromotionControlTowerModels(asRecord(project))
  const unboundFlows = flows.filter((flow) => !flow.bound && !isInitFlow(flow))
  const routeIntegrityGaps = buildRouteIntegrityGaps(project, pages, flows, componentCount)
  const gaps = [
    ...routeIntegrityGaps,
    ...buildOntologyMatchingGaps(ontologyMatching, fallbackPromotionModels.length),
    ...buildModelGaps({
      pageCount: pages.length,
      blockCount: blockNames.length,
      eventCount: runtimeContract.eventCount,
      flowCount: flows.length,
      datasourceCount: datasourceNames.length,
      capabilityCount: runtimeContract.capabilityNotes.length,
      unboundFlowCount: unboundFlows.length,
      hasSelection: selection !== null,
    }),
  ]

  return {
    pageCount: pages.length,
    blockCount: blockNames.length,
    componentCount,
    variableCount: variableNames.length,
    datasourceCount: datasourceNames.length,
    flowCount: flows.length,
    eventCount: runtimeContract.eventCount,
    designFactCount,
    ontologyMatching,
    fallbackPromotionModels,
    boundFlowCount: flows.filter((flow) => flow.bound).length,
    unboundFlows,
    gaps,
    relations: buildModelRelations({
      pageCount: pages.length,
      blockCount: blockNames.length,
      componentCount,
      variableCount: variableNames.length,
      datasourceCount: datasourceNames.length,
      flowCount: flows.length,
      eventCount: runtimeContract.eventCount,
      boundFlowCount: flows.filter((flow) => flow.bound).length,
      capabilityCount: runtimeContract.capabilityNotes.length,
      webMcpToolCount: runtimeContract.webMcpToolContracts.length,
      domainActionCount: runtimeContract.domainActionContracts.length,
      ontologyMatching,
      fallbackPromotionCount: fallbackPromotionModels.length,
    }),
    lanes: [
      {
        id: 'page',
        title: 'Page',
        subtitle: 'routes and selected page',
        icon: FileText,
        count: pages.length,
        nodes: pages.slice(0, 4).map((page) => ({
          id: `page:${page.id}`,
          label: page.label,
          detail: page.active ? 'current' : page.route,
          tone: page.active ? 'ready' : 'muted',
        })),
      },
      {
        id: 'ui',
        title: 'UI',
        subtitle: 'blocks and components',
        icon: LayoutTemplate,
        count: componentCount,
        nodes: blockNames.slice(0, 5).map((name) => ({
          id: `block:${name}`,
          label: name,
          detail: `${countBlockComponents(blocks[name])} components`,
          tone: 'ready',
        })),
      },
      {
        id: 'data',
        title: 'Data',
        subtitle: 'state and datasource contracts',
        icon: Database,
        count: variableNames.length + datasourceNames.length,
        nodes: [...variableNames.slice(0, 3), ...datasourceNames.slice(0, 2)].map((name) => ({
          id: `data:${name}`,
          label: name,
          detail: datasourceNames.includes(name) ? 'datasource' : 'variable',
          tone: 'muted',
        })),
      },
      {
        id: 'flow',
        title: 'Logic',
        subtitle: 'logic and event bindings',
        icon: GitBranch,
        count: flows.length,
        nodes: flows.slice(0, 5).map((flow) => ({
          id: `flow:${flow.name}`,
          label: flow.name,
          detail: flow.bound ? 'page trigger' : 'no page trigger',
          tone: flow.bound || isInitFlow(flow) ? 'ready' : 'review',
        })),
      },
      {
        id: 'ontology-matching',
        title: 'Source Match',
        subtitle: 'source-backed choices',
        icon: Network,
        count: ontologyMatching.decisionCount,
        nodes: ontologyMatching.labels.slice(0, 5).map((label, index) => ({
          id: `ontology-match:${index}:${label}`,
          label,
          detail: ontologyMatching.hardConstraints[index] ?? 'deterministic decision',
          tone: ontologyMatching.status,
        })),
      },
      {
        id: 'checks',
        title: 'Checks',
        subtitle: 'schema and runtime checks',
        icon: ShieldCheck,
        count: runtimeContract.capabilityNotes.length,
        nodes: runtimeContract.capabilityNotes.slice(0, 5).map((note) => ({
          id: `capability:${note}`,
          label: note,
          detail: 'runtime check',
          tone: 'ready',
        })),
      },
    ],
  }
}

function buildModelRelations(input: {
  pageCount: number
  blockCount: number
  componentCount: number
  variableCount: number
  datasourceCount: number
  flowCount: number
  eventCount: number
  boundFlowCount: number
  capabilityCount: number
  webMcpToolCount: number
  domainActionCount: number
  ontologyMatching: ProjectOntologyMatchingSummary
  fallbackPromotionCount: number
}): ModelRelation[] {
  const dataCount = input.variableCount + input.datasourceCount
  const relations: ModelRelation[] = [
    {
      id: 'page-renders-ui',
      label: 'renders',
      detail: 'A route chooses the Page, and the Page places UI blocks/components into the editable surface.',
      source: {
        title: 'Page',
        detail: `${input.pageCount} route${input.pageCount === 1 ? '' : 's'}`,
      },
      target: {
        title: 'UI blocks',
        detail: `${input.blockCount} block${input.blockCount === 1 ? '' : 's'}, ${input.componentCount} components`,
      },
      tone: input.pageCount > 0 && input.blockCount > 0 ? 'ready' : 'review',
    },
    {
      id: 'page-events-trigger-flow',
      label: 'triggers',
      detail: 'Only user-facing flows need a Page event trigger. Init/background flows can remain unbound.',
      source: {
        title: 'Page events',
        detail: `${input.eventCount} binding${input.eventCount === 1 ? '' : 's'}`,
      },
      target: {
        title: 'Logic',
        detail: `${input.boundFlowCount}/${input.flowCount} page-triggered`,
      },
      tone: input.eventCount > 0 || input.flowCount === 0 ? 'ready' : 'review',
    },
    {
      id: 'flow-updates-data',
      label: 'updates',
      detail: 'Logic can write local state, appData, datasource results, or named step results.',
      source: {
        title: 'Logic',
        detail: `${input.flowCount} flow${input.flowCount === 1 ? '' : 's'}`,
      },
      target: {
        title: 'Data contract',
        detail: dataCount > 0 ? `${dataCount} variable/datasource entries` : 'static or not defined',
      },
      tone: input.flowCount > 0 && dataCount === 0 ? 'review' : 'ready',
    },
    {
      id: 'data-binds-render',
      label: 'binds',
      detail: 'Static, default, and variable values should be distinguishable before they change Page rendering.',
      source: {
        title: 'Data contract',
        detail: dataCount > 0 ? `${dataCount} entries` : 'static/default only',
      },
      target: {
        title: 'Page render',
        detail: 'text, images, lists, and component props',
      },
      tone: dataCount > 0 ? 'ready' : 'muted',
    },
  ]

  if (input.webMcpToolCount > 0 || input.domainActionCount > 0) {
    relations.push({
      id: 'webmcp-tools-actionflow',
      label: 'uses',
      detail: 'Source tools and domain-operation steps are promoted into behavior contracts.',
      source: {
        title: 'Tools',
        detail: input.webMcpToolCount > 0
          ? `${input.webMcpToolCount} tool contract${input.webMcpToolCount === 1 ? '' : 's'}`
          : 'no tools',
      },
      target: {
        title: 'Domain actions',
        detail: input.domainActionCount > 0
          ? `${input.domainActionCount} domain action${input.domainActionCount === 1 ? '' : 's'}`
          : 'no domain actions',
      },
      tone: input.webMcpToolCount > 0 && input.domainActionCount > 0 ? 'ready' : 'review',
    })
  }

  if (
    input.ontologyMatching.decisionCount > 0 ||
    input.ontologyMatching.hardConstraints.length > 0 ||
    input.fallbackPromotionCount > 0
  ) {
    relations.push({
      id: 'intent-forces-ontology',
      label: 'guides',
      detail: [
        input.ontologyMatching.hardConstraints.length > 0
          ? `Hard constraints: ${input.ontologyMatching.hardConstraints.slice(0, 3).join(', ')}`
          : 'No hard design-system/template constraint is attached yet',
        input.fallbackPromotionCount > 0
          ? `${input.fallbackPromotionCount} review choice(s) need promotion checks`
          : 'no fallback promotion choices',
      ].join('; '),
      source: {
        title: 'User intent',
        detail: input.ontologyMatching.projectPlanDesignSystemId ?? input.ontologyMatching.projectPlanTemplateId ?? 'project plan',
      },
      target: {
        title: 'Source match',
        detail: formatOntologyMatchingHeadline(input.ontologyMatching),
      },
      tone: input.ontologyMatching.status,
    })
  }

  relations.push({
    id: 'runtime-proves-app',
    label: 'checks',
    detail: 'Checks should cover schema, renderer, and logic behavior for the current app model.',
    source: {
      title: 'UI + logic',
      detail: `${input.componentCount} components, ${input.flowCount} flows`,
    },
    target: {
      title: 'Checks',
      detail: `${input.capabilityCount} runtime check notes`,
    },
    tone: input.capabilityCount > 0 ? 'ready' : 'review',
  })

  return relations
}

function filterModelRelations(
  relations: ModelRelation[],
  query: string,
  filter: RelationFilter,
): ModelRelation[] {
  const normalizedQuery = query.trim().toLowerCase()
  return relations.filter((relation) => {
    const matchesTone = filter === 'all' || relation.tone === filter
    if (!matchesTone) return false
    if (normalizedQuery.length === 0) return true
    return [
      relation.label,
      relation.detail,
      relation.source.title,
      relation.source.detail,
      relation.target.title,
      relation.target.detail,
    ].some((value) => value.toLowerCase().includes(normalizedQuery))
  })
}

function buildProjectOntologyMatchingSummary(project: StudioProjectLike | null): ProjectOntologyMatchingSummary {
  const matchingPlan = readOntologyMatchingPlan(project)
  const compilerContract = readOntologyCompilerContract(project)
  const projectPlan = readProjectPlanRecord(project)
  const hardConstraints = asArray(matchingPlan.hardConstraints).filter((item): item is string => typeof item === 'string')
  const decisions = asArray(matchingPlan.decisions).map(asRecord).filter((item) => Object.keys(item).length > 0)
  const blockedDecisions = decisions.filter((decision) => decision.generationAllowed === false)
  const candidateDecisions = decisions.filter((decision) => stringValue(decision.candidateLifecycleStatus) === 'candidate')
  const actionFlowImpact = asRecord(matchingPlan.actionFlowImpact)
  const compilerContractPresent = compilerContract.schemaVersion === 'studio-ontology-compiler-contract.v1'
  const compilerStatus = stringValue(compilerContract.status)
  const compilerEntrypoints = asArray(compilerContract.entrypoints)
    .map(asRecord)
    .map((entrypoint) => [
      stringValue(entrypoint.name),
      stringValue(entrypoint.enforcement),
    ].filter(Boolean).join(':'))
    .filter((entrypoint): entrypoint is string => Boolean(entrypoint))
  const projectPlanDesignSystemId = stringValue(projectPlan.designSystemId)
  const projectPlanTemplateId = stringValue(projectPlan.templateId)
  const matchingPlanPresent = Object.keys(matchingPlan).length > 0 && decisions.length > 0
  const evidenceExpected = matchingPlanPresent ||
    Boolean(projectPlanDesignSystemId || projectPlanTemplateId || readOntologyRetrievalTrace(project))
  const labels = [
    ...hardConstraints,
    ...decisions.slice(0, 5).map((decision) => [
      stringValue(decision.kind) ?? 'asset',
      stringValue(decision.decision) ?? 'decision',
      stringValue(decision.selectedAssetId) ?? stringValue(decision.requirementId),
    ].filter(Boolean).join(':')),
  ].filter((label): label is string => Boolean(label))
  const actionFlowValidationRequired = actionFlowImpact.required === true ||
    asArray(actionFlowImpact.requiredValidation).includes('validateActionFlowTopologyContract')

  return {
    matchingPlanPresent,
    compilerContractPresent,
    evidenceExpected,
    hardConstraints,
    decisionCount: decisions.length,
    blockedDecisionCount: blockedDecisions.length,
    candidateDecisionCount: candidateDecisions.length,
    actionFlowValidationRequired,
    compilerStatus,
    compilerEntrypoints,
    projectPlanDesignSystemId,
    projectPlanTemplateId,
    labels: labels.length > 0
      ? labels
      : [
          projectPlanDesignSystemId ? `design-system:${projectPlanDesignSystemId}:hard` : undefined,
          projectPlanTemplateId ? `template:${projectPlanTemplateId}:hard` : undefined,
        ].filter((label): label is string => Boolean(label)),
    status: !evidenceExpected
      ? 'muted'
      : !matchingPlanPresent ||
          !compilerContractPresent ||
          compilerStatus === 'blocked' ||
          blockedDecisions.length > 0 ||
          actionFlowValidationRequired
        ? 'review'
        : 'ready',
  }
}

function buildOntologyMatchingGaps(
  matching: ProjectOntologyMatchingSummary,
  fallbackPromotionCount: number,
): ModelGap[] {
  const gaps: ModelGap[] = []
  if (matching.evidenceExpected && !matching.matchingPlanPresent) {
    gaps.push({
      id: 'ontology-matching-plan-missing',
      title: 'Source matching check missing',
      detail: 'This generated app has a project plan or retrieval trace, but no deterministic source matching plan. Regenerate or open Checks before publishing.',
      tone: 'blocker',
      actionLabel: 'Open Checks',
      action: 'validator',
    })
  }
  if (matching.evidenceExpected && !matching.compilerContractPresent) {
    gaps.push({
      id: 'ontology-compiler-contract-missing',
      title: 'Build rule check missing',
      detail: 'Template or style constrained generation must attach the build rule check before publish readiness can pass.',
      tone: 'blocker',
      actionLabel: 'Open Checks',
      action: 'validator',
    })
  }
  if (matching.compilerStatus === 'blocked') {
    gaps.push({
      id: 'ontology-compiler-contract-blocked',
      title: 'Build rule check blocked',
      detail: 'Resolve missing hard constraints or promotion checks before publishing.',
      tone: 'blocker',
      actionLabel: 'Open Checks',
      action: 'validator',
    })
  }
  if (matching.blockedDecisionCount > 0) {
    gaps.push({
      id: 'ontology-matching-candidate-only',
      title: 'Source match has review-only choices',
      detail: `${matching.blockedDecisionCount} source match choice(s) require promotion checks before publish readiness can pass.`,
      tone: 'blocker',
      actionLabel: 'Open Checks',
      action: 'validator',
    })
  }
  if (matching.actionFlowValidationRequired) {
    gaps.push({
      id: 'ontology-matching-actionflow-validation',
      title: 'Logic check required by source match',
      detail: 'The matching plan changed or rebuilt action-bearing assets. Logic topology, runtime simulation, and browser trace must stay attached.',
      tone: 'review',
      actionLabel: 'Open Logic',
      action: 'actionflow',
    })
  }
  if (fallbackPromotionCount > 0) {
    gaps.push({
      id: 'fallback-promotion-candidates',
      title: 'Review choices need promotion checks',
      detail: `${fallbackPromotionCount} project-scoped fallback choice(s) are visible. Keep them out of shared rules until rendered checks and reviewed PR handoff exist.`,
      tone: 'review',
      actionLabel: 'Open Checks',
      action: 'validator',
    })
  }
  return gaps
}

function formatOntologyMatchingHeadline(matching: ProjectOntologyMatchingSummary): string {
  if (!matching.evidenceExpected) return 'not attached'
  if (!matching.matchingPlanPresent) return 'missing matching plan'
  const blocked = matching.blockedDecisionCount > 0 ? `, ${matching.blockedDecisionCount} blocked` : ''
  const hard = matching.hardConstraints.length > 0 ? `, ${matching.hardConstraints.length} hard` : ''
  const compiler = matching.compilerContractPresent
    ? `, compiler ${matching.compilerStatus ?? 'attached'}`
    : ', compiler missing'
  return `${matching.decisionCount} decisions${blocked}${hard}${compiler}`
}

function readOntologyMatchingPlan(project: StudioProjectLike | null): Record<string, unknown> {
  const manifest = asRecord(project?.manifest)
  const metadata = asRecord(project?.metadata)
  const generationEvidence = asRecord(project?.generationEvidence)
  const manifestGenerationEvidence = asRecord(manifest.generationEvidence)
  const candidates = [
    project?.ontologyMatchingPlan,
    asRecord(project?.ontologyRetrievalTrace).ontologyMatchingPlan,
    generationEvidence.ontologyMatchingPlan,
    asRecord(generationEvidence.ontologyRetrievalTrace).ontologyMatchingPlan,
    asRecord(generationEvidence.uiOntology).ontologyMatchingPlan,
    manifest.ontologyMatchingPlan,
    asRecord(manifest.ontologyRetrievalTrace).ontologyMatchingPlan,
    manifestGenerationEvidence.ontologyMatchingPlan,
    asRecord(manifestGenerationEvidence.ontologyRetrievalTrace).ontologyMatchingPlan,
    asRecord(manifestGenerationEvidence.uiOntology).ontologyMatchingPlan,
    asRecord(metadata.ontologyRetrievalTrace).ontologyMatchingPlan,
  ]
  return candidates.map(asRecord).find((candidate) => (
    candidate.schemaVersion === 'cogo.application-ontology-matching.v1' ||
    asArray(candidate.decisions).length > 0
  )) ?? {}
}

function readOntologyCompilerContract(project: StudioProjectLike | null): Record<string, unknown> {
  const manifest = asRecord(project?.manifest)
  const metadata = asRecord(project?.metadata)
  const generationEvidence = asRecord(project?.generationEvidence)
  const manifestGenerationEvidence = asRecord(manifest.generationEvidence)
  const candidates = [
    project?.ontologyCompilerContract,
    asRecord(project?.ontologyRetrievalTrace).ontologyCompilerContract,
    generationEvidence.ontologyCompilerContract,
    asRecord(generationEvidence.ontologyRetrievalTrace).ontologyCompilerContract,
    asRecord(generationEvidence.uiOntology).ontologyCompilerContract,
    manifest.ontologyCompilerContract,
    asRecord(manifest.ontologyRetrievalTrace).ontologyCompilerContract,
    manifestGenerationEvidence.ontologyCompilerContract,
    asRecord(manifestGenerationEvidence.ontologyRetrievalTrace).ontologyCompilerContract,
    asRecord(manifestGenerationEvidence.uiOntology).ontologyCompilerContract,
    asRecord(metadata.ontologyRetrievalTrace).ontologyCompilerContract,
  ]
  return candidates.map(asRecord).find((candidate) => (
    candidate.schemaVersion === 'studio-ontology-compiler-contract.v1' ||
    stringValue(candidate.status)
  )) ?? {}
}

function readProjectPlanRecord(project: StudioProjectLike | null): Record<string, unknown> {
  const manifest = asRecord(project?.manifest)
  const settings = asRecord(project?.settings)
  const manifestSettings = asRecord(manifest.settings)
  const metadata = asRecord(project?.metadata)
  const candidates = [
    project?.projectPlan,
    project?.studioProjectPlan,
    manifest.projectPlan,
    manifest.studioProjectPlan,
    settings.studioProjectPlan,
    settings.projectPlan,
    manifestSettings.studioProjectPlan,
    manifestSettings.projectPlan,
    metadata.studioProjectPlan,
    metadata.projectPlan,
  ]
  return candidates.map(asRecord).find((candidate) => (
    stringValue(candidate.intent) ||
    stringValue(candidate.designSystemId) ||
    stringValue(candidate.templateId)
  )) ?? {}
}

function readOntologyRetrievalTrace(project: StudioProjectLike | null): Record<string, unknown> | null {
  const manifest = asRecord(project?.manifest)
  const metadata = asRecord(project?.metadata)
  const generationEvidence = asRecord(project?.generationEvidence)
  const manifestGenerationEvidence = asRecord(manifest.generationEvidence)
  const candidates = [
    project?.ontologyRetrievalTrace,
    generationEvidence.ontologyRetrievalTrace,
    manifest.ontologyRetrievalTrace,
    manifestGenerationEvidence.ontologyRetrievalTrace,
    metadata.ontologyRetrievalTrace,
  ]
  return candidates.map(asRecord).find((candidate) => Object.keys(candidate).length > 0) ?? null
}

function buildRouteIntegrityGaps(
  project: StudioProjectLike | null,
  pages: PageSummary[],
  flows: FlowSummary[],
  componentCount: number,
): ModelGap[] {
  const manifest = asRecord(project?.manifest)
  const pageIds = new Set(pages.map((page) => page.id))
  const defaultPageId = stringValue(manifest.defaultPageId)
  const defaultRoute = stringValue(manifest.defaultRoute)
  const gaps: ModelGap[] = buildStatisticsIntegrityGaps(project, pages, flows, componentCount)

  if (defaultPageId && !pageIds.has(defaultPageId)) {
    gaps.push({
      id: 'default-page-missing',
      title: 'Default page does not resolve',
      detail: `manifest.defaultPageId is ${defaultPageId}, but that page is not present in manifest.pages.`,
      tone: 'blocker',
      actionLabel: 'Open Checks',
      action: 'validator',
    })
  }

  if (defaultRoute) {
    const defaultPage = defaultPageId
      ? pages.find((page) => page.id === defaultPageId)
      : pages[0]
    const routeExists = pages.some((page) => page.route === defaultRoute)
    if (!routeExists) {
      gaps.push({
        id: 'default-route-missing',
        title: 'Default route does not resolve',
        detail: `manifest.defaultRoute is ${defaultRoute}, but no page exposes that route.`,
        tone: 'blocker',
        actionLabel: 'Open Checks',
        action: 'validator',
      })
    } else if (defaultPage && defaultPage.route !== defaultRoute) {
      gaps.push({
        id: 'default-route-mismatch',
        title: 'Default route and page disagree',
        detail: `manifest.defaultPageId points to ${defaultPage.id} (${defaultPage.route}), but manifest.defaultRoute is ${defaultRoute}.`,
        tone: 'blocker',
        actionLabel: 'Open Checks',
        action: 'validator',
      })
    }
  }

  const unknownRouteIds = Array.from(new Set(
    flows
      .map((flow) => flow.routeId)
      .filter((routeId): routeId is string => Boolean(routeId && !pageIds.has(routeId))),
  ))
  if (unknownRouteIds.length > 0) {
    gaps.push({
      id: 'flow-route-missing',
      title: 'Logic references missing page route',
      detail: `Flow routeId values are not present in manifest.pages: ${unknownRouteIds.slice(0, 6).join(', ')}.`,
      tone: 'blocker',
      actionLabel: 'Open Logic',
      action: 'actionflow',
    })
  }

  if (hasExpoIndexSourceRoute(project, pages) && !hasSourceRouteContract(project, pages, manifest)) {
    gaps.push({
      id: 'source-route-contract-missing',
      title: 'Source route default is not proven',
      detail: 'This source-derived Expo app includes app/index.tsx, but the Coogo project does not declare defaultRoute/defaultPageId or redirectAliases. Studio cannot prove whether index is a real page or a redirect alias.',
      tone: 'review',
      actionLabel: 'Open Checks',
      action: 'validator',
    })
  }

  return gaps
}

function buildStatisticsIntegrityGaps(
  project: StudioProjectLike | null,
  pages: PageSummary[],
  flows: FlowSummary[],
  componentCount: number,
): ModelGap[] {
  const manifestStatistics = asRecord(asRecord(project?.manifest).statistics)
  const ontologyStatistics = asRecord(asRecord(project?.ontology).statistics)
  const mismatches: string[] = []
  collectCountMismatch(mismatches, 'manifest.statistics.pageCount', manifestStatistics.pageCount, pages.length)
  collectCountMismatch(mismatches, 'manifest.statistics.nodeCount', manifestStatistics.nodeCount, componentCount)
  collectCountMismatch(mismatches, 'manifest.statistics.actionFlowCount', manifestStatistics.actionFlowCount, flows.length)
  collectCountMismatch(mismatches, 'ontology.statistics.pageCount', ontologyStatistics.pageCount, pages.length)
  collectCountMismatch(mismatches, 'ontology.statistics.nodeCount', ontologyStatistics.nodeCount, componentCount)
  collectCountMismatch(mismatches, 'ontology.statistics.actionFlowCount', ontologyStatistics.actionFlowCount, flows.length)
  if (mismatches.length === 0) return []
  return [{
    id: 'statistics-model-mismatch',
    title: 'Manifest statistics disagree with application model',
    detail: `${mismatches.slice(0, 6).join('; ')}. Recompute statistics after route/page/logic post-processing before Studio import/export checks are accepted.`,
    tone: 'blocker',
    actionLabel: 'Open Checks',
    action: 'validator',
  }]
}

function collectCountMismatch(
  mismatches: string[],
  label: string,
  value: unknown,
  actual: number,
): void {
  const expected = numberValue(value)
  if (expected !== undefined && expected !== actual) {
    mismatches.push(`${label}=${expected} actual=${actual}`)
  }
}

function hasExpoIndexSourceRoute(project: StudioProjectLike | null, pages: PageSummary[]): boolean {
  const sourceFiles = [
    ...pages.map((page) => page.sourceFile),
    ...asArray(asRecord(project?.ontology).sourceFiles).map((entry) => stringValue(asRecord(entry).file)),
  ].filter((file): file is string => Boolean(file))
  return sourceFiles.some((file) => normalizeSourcePath(file).endsWith('/app/index.tsx'))
}

function hasSourceRouteContract(
  project: StudioProjectLike | null,
  pages: PageSummary[],
  manifest: Record<string, unknown>,
): boolean {
  if (stringValue(manifest.defaultPageId) && stringValue(manifest.defaultRoute)) return true
  if (pages.some((page) => asArray(page.metadata.redirectAliases).length > 0)) return true
  return asArray(asRecord(project?.ontology).pages).some((page) => {
    const pageRecord = asRecord(page)
    const metadata = asRecord(pageRecord.metadata)
    return pageRecord.defaultRoute === true || stringValue(pageRecord.defaultRoute) !== undefined || asArray(metadata.redirectAliases).length > 0
  })
}

function normalizeSourcePath(file: string): string {
  return file.replace(/\\/g, '/')
}

function buildModelGaps(input: {
  pageCount: number
  blockCount: number
  eventCount: number
  flowCount: number
  datasourceCount: number
  capabilityCount: number
  unboundFlowCount: number
  hasSelection: boolean
}): ModelGap[] {
  const gaps: ModelGap[] = []
  if (input.pageCount === 0) {
    gaps.push({
      id: 'no-page',
      title: 'No page model',
      detail: 'Create or open a Page before reviewing app-level context.',
      tone: 'blocker',
      actionLabel: 'Open Page',
      action: 'page',
    })
  }
  if (input.blockCount === 0) {
    gaps.push({
      id: 'no-ui',
      title: 'No UI blocks',
      detail: 'Page rendering needs at least one UI block before variables and flows can be grounded.',
      tone: 'blocker',
      actionLabel: 'Open Page',
      action: 'page',
    })
  }
  if (input.eventCount === 0 && input.flowCount > 0) {
    gaps.push({
      id: 'flows-without-events',
      title: 'Logic has no Page trigger',
      detail: 'There are logic flows, but no Page event bindings. This is fine for init/background work; otherwise connect from Page selection.',
      tone: 'review',
      actionLabel: 'Open Logic',
      action: 'actionflow',
    })
  }
  if (input.unboundFlowCount > 0) {
    gaps.push({
      id: 'unbound-flows',
      title: 'Review flows without Page trigger',
      detail: `${input.unboundFlowCount} flow(s) are not started by a Page element. Bind only the flows that represent user interaction.`,
      tone: 'review',
      actionLabel: 'Open Logic',
      action: 'actionflow',
    })
  }
  if (input.datasourceCount === 0) {
    gaps.push({
      id: 'no-datasource',
      title: 'No datasource contract',
      detail: 'Static apps can skip this. Dynamic apps should define the data source or state contract before binding Page content.',
      tone: 'info',
      actionLabel: 'Open Data',
      action: 'data',
    })
  }
  if (input.capabilityCount === 0) {
    gaps.push({
      id: 'no-runtime-proof',
      title: 'No runtime check notes',
      detail: 'Open Checks after Page and Logic changes so schema, renderer, and runtime details stay aligned.',
      tone: 'info',
      actionLabel: 'Open Checks',
      action: 'validator',
    })
  }
  if (!input.hasSelection) {
    gaps.push({
      id: 'no-selection',
      title: 'No current Page context',
      detail: 'Choose a Page element when you want to review variable or logic bindings for a specific component.',
      tone: 'info',
      actionLabel: 'Open Page',
      action: 'page',
    })
  }
  return gaps
}

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : []
}

function numberValue(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

function collectPages(project: StudioProjectLike | null, activePageId: string | null) {
  const manifest = asRecord(project?.manifest)
  const rawPages = asArray(manifest.pages).length > 0 ? asArray(manifest.pages) : asArray(project?.pages)
  const pages: PageSummary[] = rawPages
    .map((page, index) => {
      const record = asRecord(page)
      const id = stringValue(record.id) ?? `page-${index + 1}`
      const route = stringValue(record.route) ?? stringValue(record.path) ?? id
      const label = stringValue(record.title) ?? stringValue(record.name) ?? id
      return {
        id,
        label,
        route,
        active: activePageId ? id === activePageId : index === 0,
        sourceFile: stringValue(record.sourceFile),
        metadata: asRecord(record.metadata),
      }
    })
  if (pages.length === 0 && activePageId) {
    return [{ id: activePageId, label: activePageId, route: activePageId, active: true, metadata: {} }]
  }
  return pages
}

function collectFlows(project: StudioProjectLike | null): FlowSummary[] {
  const blocks = asRecord(project?.blocks)
  const topLevelFlows = asRecord(project?.flows)
  const bound = collectBoundFlowNames(blocks)
  const flows = new Map<string, FlowSummary>()

  addFlowRecords(flows, topLevelFlows, bound)
  for (const block of Object.values(blocks)) {
    const blockRecord = asRecord(block)
    const flowRoot = blockRecord.flow
    const nestedFlows = Object.keys(asRecord(asRecord(flowRoot).flows)).length > 0 ? asRecord(asRecord(flowRoot).flows) : flowRoot
    addFlowRecords(flows, nestedFlows, bound)
  }

  return Array.from(flows.values()).sort((a, b) => a.name.localeCompare(b.name))
}

function addFlowRecords(
  flows: Map<string, FlowSummary>,
  flowMap: unknown,
  bound: Set<string>,
) {
  const entries = Array.isArray(flowMap)
    ? flowMap.map((value, index) => [stringValue(asRecord(value).id) ?? `flow-${index + 1}`, value] as const)
    : Object.entries(asRecord(flowMap))
  for (const [name, value] of entries) {
    const flow = asRecord(value)
    if (Object.keys(flow).length === 0) continue
    const normalized = normalizeFlowName(name)
    if (flows.has(normalized)) continue
    flows.set(normalized, {
      name,
      trigger: stringValue(flow.trigger),
      bound: bound.has(normalized),
      routeId: stringValue(flow.routeId) ?? routeIdFromTrigger(flow.trigger),
    })
  }
}

function routeIdFromTrigger(value: unknown): string | undefined {
  const trigger = asRecord(value)
  return stringValue(trigger.routeId)
}

function collectBoundFlowNames(blocks: Record<string, unknown>): Set<string> {
  const bound = new Set<string>()
  for (const block of Object.values(blocks)) {
    collectFlowRefs(asRecord(block).events, bound)
  }
  return bound
}

function collectFlowRefs(value: unknown, bound: Set<string>) {
  if (typeof value === 'string') {
    bound.add(normalizeFlowName(value))
    return
  }
  if (Array.isArray(value)) {
    for (const item of value) collectFlowRefs(item, bound)
    return
  }
  const record = asRecord(value)
  for (const [key, nested] of Object.entries(record)) {
    if (
      ['flow', 'flowId', 'flowName', 'actionFlow', 'actionFlowId', 'targetFlow'].includes(key) &&
      typeof nested === 'string'
    ) {
      bound.add(normalizeFlowName(nested))
      continue
    }
    collectFlowRefs(nested, bound)
  }
}

function countBlockComponents(block: unknown): number {
  const root = asRecord(asRecord(block).ui).root
  return countUiNode(root)
}

function countUiNode(node: unknown): number {
  const record = asRecord(node)
  if (Object.keys(record).length === 0) return 0
  return 1 + asArray(record.children).reduce<number>((sum, child) => sum + countUiNode(child), 0)
}

function collectVariableNames(
  blocks: Record<string, unknown>,
  runtimeContract: RuntimeContractSummary,
): string[] {
  const names = new Set<string>()
  for (const [blockName, block] of Object.entries(blocks)) {
    const state = asRecord(asRecord(block).state)
    for (const scope of ['localState', 'computed', 'props', 'appData']) {
      const vars = asRecord(state[scope])
      for (const name of Object.keys(vars)) names.add(`${blockName}.${name}`)
    }
  }
  for (const name of runtimeContract.appDataWriteTargets) names.add(name)
  for (const name of runtimeContract.localDbWriteTargets) names.add(name)
  for (const name of runtimeContract.computedNames) names.add(name)
  return Array.from(names).sort((a, b) => a.localeCompare(b))
}

function collectDatasourceNames(project: StudioProjectLike | null): string[] {
  const names = new Set<string>()
  const manifest = asRecord(project?.manifest)
  for (const key of Object.keys(asRecord(manifest.datasources))) names.add(key)
  for (const key of Object.keys(asRecord(project?.data))) names.add(key)
  for (const block of Object.values(asRecord(project?.blocks))) {
    for (const key of Object.keys(asRecord(asRecord(block).data))) names.add(key)
  }
  return Array.from(names).sort((a, b) => a.localeCompare(b))
}

function countDesignFacts(project: StudioProjectLike | null): number {
  const tokenCount = Object.keys(asRecord(project?.tokens)).length
  const manifest = asRecord(project?.manifest)
  const theme = asRecord(manifest.theme)
  const designSystem = asRecord(manifest.designSystem)
  return tokenCount + Object.keys(theme).length + Object.keys(designSystem).length
}

function normalizeFlowName(name: string): string {
  const normalized = name.trim().toLowerCase()
  const parts = normalized.split('.').filter(Boolean)
  return parts[parts.length - 1] ?? normalized
}

function isInitFlow(flow: FlowSummary): boolean {
  return flow.trigger === 'init' || flow.name.toLowerCase().startsWith('init')
}

function stringValue(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined
}
